import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Input,
  Stack,
  Typography,
} from '@mui/joy';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPen, FaTruck } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';
import ProjectFolderViewer from '../components/ProjectFolderViewer';
import { useProject } from '../hooks/useProjects';
import { useCustomerStore } from '../store/customerStore';
import { useProfile } from '../hooks/useProfile';
import type { Customer } from '../services/customerService';
import type { Project } from '../types/project';
import { directSupabase } from '../lib/supabaseClient';
import type { ProductionDateEntry, WorkflowTask } from '../components/ProjectWorkflow';
import {
  applyWorkflowTaskChange,
  getFullWorkflowTasksForMode,
  getWorkflowFlagPatch,
  workspaceLifecycleStatus,
} from '../utils/workflowUtils';
import AftercareStagePanel from './project-workspace/AftercareStagePanel';
import ImplementationStagePanel from './project-workspace/ImplementationStagePanel';
import InitiationStagePanel from './project-workspace/InitiationStagePanel';
import QuotesStagePanel, { type QuotesLeaveHandle } from './project-workspace/QuotesStagePanel';
import SupplierStagePanel from './project-workspace/SupplierStagePanel';
import { loadWorkspaceSnapshots } from './project-workspace/workspaceDashboard.service';
import type { WorkspaceSnapshot } from './project-workspace/workspaceDashboard.model';
import { applyRecordTicks } from './project-workspace/checklistRecords';
import { splitName } from './project-workspace/workspaceBillingIdentity';
import WorkspaceCustomerCard from './project-workspace/WorkspaceCustomerCard';
import { ProductTypeChips } from './project-workspace/WorkspaceProductTypesCard';
import { areAllProjectJobCardsComplete } from './project-workspace/jobCard.service';
import { assertJobRecordAllowsComplete } from '../services/jobReviews.service';
import {
  EVENT_LINKED_TASKS,
  WORKSPACE_STAGES,
  isWorkspaceStageId,
  type WorkspaceStageId,
} from './project-workspace/types';
import { setKeyDateManualComplete } from './project-workspace/keyDateCompletion';
import { ensureProjectLifecycleRef } from './project-workspace/workspaceLifecycleRef';

function resolveCustomer(
  customers: Customer[],
  project: { customer_id?: string | null; customer_email?: string | null; customer_name?: string | null; created_at: string; updated_at: string }
): Customer | null {
  const byId = customers.find((customer) => customer.id?.toString() === project.customer_id?.toString());
  if (byId) return byId;
  if (project.customer_email) {
    const byEmail = customers.find((customer) => customer.email === project.customer_email);
    if (byEmail) return byEmail;
  }
  if (!project.customer_name && !project.customer_email) return null;
  return {
    id: project.customer_id || '',
    name: project.customer_name || 'Unknown',
    email: project.customer_email || null,
    phone: '',
    address: '',
    created_at: project.created_at,
    updated_at: project.updated_at,
    source: 'local',
  };
}

function formatCreatedDate(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function statusLabel(project: Project) {
  return workspaceLifecycleStatus(project);
}

const ProjectWorkspacePage: React.FC = () => {
  const { projectId, stage } = useParams<{ projectId: string; stage?: string }>();
  const navigate = useNavigate();
  const { project, loading, error } = useProject(projectId);
  const { customers, loadCustomers } = useCustomerStore();
  const { isAdmin } = useProfile();
  const [tasks, setTasks] = useState<WorkflowTask[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [projectPatch, setProjectPatch] = useState<Partial<Project>>({});
  const [calendarSyncKey, setCalendarSyncKey] = useState(0);
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const [titleSaving, setTitleSaving] = useState(false);
  const quotesLeaveRef = useRef<QuotesLeaveHandle | null>(null);
  const invoicesLeaveRef = useRef<QuotesLeaveHandle | null>(null);
  const tasksRef = useRef<WorkflowTask[]>([]);
  // Where the job is: the same answer as the Board, from quotes, invoices, orders and bookings
  const [snapshot, setSnapshot] = useState<WorkspaceSnapshot | null>(null);
  const [snapshotKey, setSnapshotKey] = useState(0);
  const snapshotRef = useRef<WorkspaceSnapshot | null>(null);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    setProjectPatch({});
    setTitleDraft(null);
  }, [project?.id]);

  useEffect(() => {
    if (!project?.id) return;
    const current = String(projectPatch.lifecycle_ref ?? project.lifecycle_ref ?? '').trim();
    if (current) return;
    let cancelled = false;
    ensureProjectLifecycleRef(project.id, project.lifecycle_ref, { mint: false })
      .then((ref) => {
        if (!cancelled && ref) setProjectPatch((prev) => ({ ...prev, lifecycle_ref: ref }));
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Failed to assign a lifecycle ref';
        if (/lifecycle_ref does not exist/i.test(message)) return;
        if (!cancelled) setSaveError(message);
      });
    return () => {
      cancelled = true;
    };
  }, [project?.id, project?.lifecycle_ref, projectPatch.lifecycle_ref]);

  useEffect(() => {
    if (!project) return;
    const next = getFullWorkflowTasksForMode(project.workflow_tasks || [], project.supply_only || false, project);
    tasksRef.current = next;
    setTasks(next);
    // Hydrate once per project so optimistic ticks are not reset by object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const activeStage: WorkspaceStageId = isWorkspaceStageId(stage) ? stage : 'initiation';
  const viewProject = useMemo(
    () => (project ? { ...project, ...projectPatch } : null),
    [project, projectPatch]
  );
  const lifecycleKey = viewProject ? workspaceLifecycleStatus(viewProject) : '';
  useEffect(() => {
    if (!viewProject?.id) return;
    let cancelled = false;
    loadWorkspaceSnapshots([viewProject as any])
      .then((snapshots) => {
        if (cancelled) return;
        const next = snapshots[viewProject.id] || null;
        snapshotRef.current = next;
        setSnapshot(next);
      })
      .catch((err) => console.warn('Failed to load workspace stage', err));
    return () => {
      cancelled = true;
    };
    // Re-read when the tab changes (e.g. back from Invoices), a tick is saved, or dates change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewProject?.id, lifecycleKey, activeStage, snapshotKey, calendarSyncKey]);
  const customer = useMemo(
    () => (viewProject ? resolveCustomer(customers, viewProject) : null),
    [customers, viewProject]
  );

  const handleProjectPatch = useCallback(async (patch: Partial<Project>) => {
    if (!projectId) return;
    const previousCustomerId = (projectPatch.customer_id ?? project?.customer_id) ?? null;
    const { error: updateError } = await directSupabase
      .from('projects')
      .update(patch)
      .eq('id', projectId);
    if (updateError) throw new Error(updateError.message);
    setProjectPatch((prev) => ({ ...prev, ...patch }));
    if (patch.customer_id != null && patch.customer_id !== previousCustomerId) {
      // Documents carry their own customer, which is who they are emailed to: move the
      // workspace's documents across too, except invoices FreeAgent already holds against a contact
      const { firstName, lastName } = splitName(patch.customer_name);
      const { error: docsError } = await directSupabase
        .from('documents')
        .update({
          customer_id: patch.customer_id,
          customer_first_name: firstName,
          customer_last_name: lastName,
          customer_title: null,
        })
        .eq('project_id', projectId)
        .neq('status', 'void')
        .is('freeagent_invoice_url', null);
      if (docsError) throw new Error(`Customer changed, but its documents were not moved across: ${docsError.message}`);
    }
    if (patch.customer_id != null) {
      const { emitProjectCustomerChangedActivity } = await import('../services/activityEvents.service');
      emitProjectCustomerChangedActivity({
        projectId,
        previousCustomerId,
        customerId: patch.customer_id,
        customerName: patch.customer_name,
      });
    }
  }, [project?.customer_id, projectId, projectPatch.customer_id]);

  const persistTasks = useCallback(
    async (requested: WorkflowTask[]) => {
      // Steps backed by a record (paid deposit, booking, balance invoice) are saved ticked too,
      // so the stored checklist and its flags agree with the records
      const next = applyRecordTicks(requested, snapshotRef.current?.recordTicks);
      if (!projectId || !project) return;
      const current = { ...project, ...projectPatch };
      const flags = getWorkflowFlagPatch(next, current.supply_only || false, current);
      const prevStatus = current.project_status || 'active';
      const nowIso = new Date().toISOString();
      const timestamps: {
        status_changed_at?: string;
        completed_at?: string | null;
        cancelled_at?: string | null;
      } = {};
      if (prevStatus !== flags.project_status) {
        timestamps.status_changed_at = nowIso;
        timestamps.completed_at = flags.project_status === 'completed' ? nowIso : null;
        timestamps.cancelled_at = flags.project_status === 'cancelled' ? nowIso : null;
      }
      if (flags.project_status === 'completed' && prevStatus !== 'completed') {
        await assertJobRecordAllowsComplete(projectId);
      }
      const { error: updateError } = await directSupabase
        .from('projects')
        .update({
          workflow_tasks: next,
          ...flags,
          ...timestamps,
        })
        .eq('id', projectId);
      if (updateError) throw new Error(updateError.message);
      setProjectPatch((prev) => ({ ...prev, ...flags, ...timestamps }));
      setSnapshotKey((value) => value + 1);
    },
    [project, projectId, projectPatch]
  );

  const handleTaskChange = async (
    taskName: string,
    completed: boolean,
    options?: { skipKeyDateSync?: boolean }
  ) => {
    const source = tasksRef.current.length > 0
      ? tasksRef.current
      : getFullWorkflowTasksForMode(project?.workflow_tasks || [], project?.supply_only || false, project || undefined);
    if (source.some((task) => task.task === taskName && task.completed === completed)) return;
    const previous = source;
    const next = applyWorkflowTaskChange(source, taskName, completed);
    tasksRef.current = next;
    setTasks(next);
    setSaveError(null);
    setSaving(true);
    try {
      await persistTasks(next);
      const eventType = EVENT_LINKED_TASKS[taskName];
      if (eventType && projectId && !options?.skipKeyDateSync) {
        await setKeyDateManualComplete(projectId, eventType, completed);
        setCalendarSyncKey((value) => value + 1);
      }
    } catch (err) {
      tasksRef.current = previous;
      setTasks(previous);
      setSaveError(err instanceof Error ? err.message : 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleProductionDatesChange = async (productionDates: ProductionDateEntry[]) => {
    const source = tasksRef.current.length > 0
      ? tasksRef.current
      : getFullWorkflowTasksForMode(project?.workflow_tasks || [], project?.supply_only || false, project || undefined);
    const hasValidDates = productionDates.some((entry) => entry.date && entry.supplierId);
    const previous = source;
    const next = source.some((task) => task.task === 'Production Date')
      ? source.map((task) => (
        task.task === 'Production Date'
          ? { ...task, productionDates, completed: hasValidDates }
          : task
      ))
      : [...source, { task: 'Production Date', productionDates, completed: hasValidDates }];
    tasksRef.current = next;
    setTasks(next);
    setSaveError(null);
    setSaving(true);
    try {
      await persistTasks(next);
    } catch (err) {
      tasksRef.current = previous;
      setTasks(previous);
      setSaveError(err instanceof Error ? err.message : 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  if (stage === 'visual') {
    return <Navigate to={`/projects/${projectId}/workspace/quotes`} replace />;
  }

  if (stage && !isWorkspaceStageId(stage)) {
    return <Navigate to={`/projects/${projectId}/workspace/initiation`} replace />;
  }

  if (loading && !project) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (error || !project || !projectId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert color="danger">{error || 'Project not found'}</Alert>
        <Button
          sx={{ mt: 2 }}
          variant="plain"
          startDecorator={<IconWrapper icon={FaArrowLeft} />}
          component={Link}
          to="/workspaces"
        >
          Back to projects
        </Button>
      </Box>
    );
  }

  const liveProject = viewProject || project;
  const title = `${liveProject.supply_only && !liveProject.project_name?.startsWith('SUPPLY ONLY: ') ? 'SUPPLY ONLY: ' : ''}${liveProject.project_name || 'Untitled Project'}`;
  const created = formatCreatedDate(liveProject.created_at);
  const status = statusLabel(liveProject);
  const productTypes = (liveProject.product_types || []).map((type) => String(type).trim()).filter(Boolean);
  const trimmedTitleDraft = (titleDraft ?? '').trim();

  const saveTitle = async () => {
    if (titleDraft == null || !trimmedTitleDraft) return;
    if (trimmedTitleDraft === (liveProject.project_name || '')) {
      setTitleDraft(null);
      return;
    }
    setTitleSaving(true);
    setSaveError(null);
    try {
      await handleProjectPatch({ project_name: trimmedTitleDraft });
      setTitleDraft(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to rename workspace');
    } finally {
      setTitleSaving(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 1.5, md: 2.5 }, pb: 4, width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
      {saveError && (
        <Alert color="danger" sx={{ mb: 2 }}>{saveError}</Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={1}
        sx={{ mb: 0.75 }}
      >
        {titleDraft != null ? (
          <Stack direction="row" gap={1} alignItems="center" sx={{ width: '100%', maxWidth: 720, minWidth: 0 }}>
            <Input
              autoFocus
              value={titleDraft}
              disabled={titleSaving}
              onChange={(event) => setTitleDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') { event.preventDefault(); void saveTitle(); }
                if (event.key === 'Escape') setTitleDraft(null);
              }}
              slotProps={{ input: { 'aria-label': 'Workspace title' } }}
              sx={{ flex: 1, minWidth: 0, fontSize: { xs: '1.1rem', sm: '1.35rem' }, fontWeight: 700 }}
            />
            <Button size="sm" loading={titleSaving} disabled={!trimmedTitleDraft} onClick={() => { void saveTitle(); }}>
              Save
            </Button>
            <Button size="sm" variant="plain" color="neutral" disabled={titleSaving} onClick={() => setTitleDraft(null)}>
              Cancel
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" gap={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              level="h1"
              sx={{
                fontSize: { xs: '1.45rem', sm: '1.85rem' },
                m: 0,
                lineHeight: 1.25,
                fontWeight: 700,
              }}
            >
              {title}
            </Typography>
            <IconButton
              size="sm"
              variant="plain"
              color="neutral"
              aria-label="Edit workspace title"
              title="Edit workspace title"
              onClick={() => setTitleDraft(liveProject.project_name || '')}
            >
              <IconWrapper icon={FaPen} />
            </IconButton>
          </Stack>
        )}
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {liveProject.lifecycle_ref && (
            <Chip size="sm" variant="outlined" title="Workspace lifecycle reference">
              {liveProject.lifecycle_ref}
            </Chip>
          )}
          <Chip
            size="sm"
            variant="soft"
            color={status === 'Cancelled' ? 'danger' : status === 'Completed' ? 'success' : 'success'}
            title="Project status: active, completed, or cancelled"
          >
            {status}
          </Chip>
          {created && (
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              Created {created}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 0.75, mb: 2 }}>
        {liveProject.supply_only && (
          <Chip size="sm" variant="soft" startDecorator={<IconWrapper icon={FaTruck} />}>
            Supply only
          </Chip>
        )}
        <ProductTypeChips selected={productTypes} readOnly />
      </Stack>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 'lg',
          bgcolor: 'background.surface',
          p: { xs: 1.5, md: 2 },
          mb: 2,
        }}
      >
        <WorkspaceCustomerCard
          project={liveProject}
          customer={customer}
          allowChange
          onProjectPatch={handleProjectPatch}
          requestLeave={(proceed) => {
            if (activeStage === 'quotes' && quotesLeaveRef.current) {
              quotesLeaveRef.current.requestLeave(proceed);
              return;
            }
            if (activeStage === 'invoices' && invoicesLeaveRef.current) {
              invoicesLeaveRef.current.requestLeave(proceed);
              return;
            }
            proceed();
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: { xs: 0.75, md: 1 },
          p: { xs: 1, md: 1.25 },
          mb: 2,
          borderRadius: 'lg',
          bgcolor: 'background.surface',
          border: '1px solid',
          borderColor: 'divider',
          overflowX: 'auto',
        }}
      >
        {WORKSPACE_STAGES.map((item) => {
          const selected = activeStage === item.id;
          return (
            <React.Fragment key={item.id}>
              {item.id === 'aftercare' && (
                <Box
                  sx={{
                    width: '1px',
                    alignSelf: 'stretch',
                    bgcolor: 'divider',
                    my: 0.5,
                    mx: 0.25,
                    flexShrink: 0,
                  }}
                />
              )}
              <Button
                variant="plain"
                color="neutral"
                onClick={() => {
                  if (selected) return;
                  const go = () => navigate(`/projects/${project.id}/workspace/${item.id}`);
                  if (activeStage === 'quotes' && quotesLeaveRef.current) {
                    quotesLeaveRef.current.requestLeave(go);
                    return;
                  }
                  if (activeStage === 'invoices' && invoicesLeaveRef.current) {
                    invoicesLeaveRef.current.requestLeave(go);
                    return;
                  }
                  go();
                }}
                sx={{
                  flex: '1 1 0',
                  minWidth: 108,
                  minHeight: 76,
                  py: 1.25,
                  px: 1,
                  borderRadius: 'lg',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  border: '1px solid',
                  borderColor: selected ? 'var(--jarvis-stage-selected-border)' : 'var(--jarvis-stage-border)',
                  bgcolor: selected ? 'var(--jarvis-stage-selected-bg)' : 'var(--jarvis-stage-bg)',
                  color: selected ? 'var(--jarvis-stage-selected-text)' : 'var(--jarvis-stage-text)',
                  fontWeight: selected ? 700 : 600,
                  whiteSpace: 'normal',
                  '--Button-gap': '6px',
                  transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
                  '&:hover': {
                    bgcolor: 'var(--jarvis-hover-bg)',
                    borderColor: 'var(--jarvis-hover-border)',
                    color: 'var(--jarvis-hover-text)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: selected ? 'var(--jarvis-stage-selected-badge-bg)' : 'var(--jarvis-stage-badge-bg)',
                    color: selected ? 'var(--jarvis-stage-selected-badge-text)' : 'var(--jarvis-stage-badge-text)',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    transition: 'background-color 120ms ease, color 120ms ease',
                    '.MuiButton-root:hover &': {
                      bgcolor: 'var(--jarvis-accent)',
                      color: 'var(--jarvis-hover-text)',
                    },
                  }}
                >
                  {item.n}
                </Box>
                <Typography
                  level="body-sm"
                  fontWeight="lg"
                  sx={{
                    lineHeight: 1.25,
                    color: 'inherit',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                  }}
                >
                  {item.label}
                </Typography>
              </Button>
            </React.Fragment>
          );
        })}
      </Box>

      {activeStage === 'initiation' && (
        <InitiationStagePanel
          project={liveProject}
          customer={customer}
          tasks={tasks}
          onProjectPatch={handleProjectPatch}
          onTaskChange={handleTaskChange}
          saving={saving}
          calendarSyncKey={calendarSyncKey}
          onLinkedTaskChange={(taskName, completed) => handleTaskChange(taskName, completed, { skipKeyDateSync: true })}
          snapshot={snapshot}
        />
      )}
      {activeStage === 'quotes' && (
        <QuotesStagePanel
          key={`quotes-${liveProject.id}`}
          project={liveProject}
          customer={customer}
          isAdmin={isAdmin}
          section="quotes"
          leaveGuardRef={quotesLeaveRef}
        />
      )}
      {activeStage === 'invoices' && (
        <QuotesStagePanel
          key={`invoices-${liveProject.id}`}
          project={liveProject}
          customer={customer}
          isAdmin={isAdmin}
          section="invoices"
          leaveGuardRef={invoicesLeaveRef}
        />
      )}
      {activeStage === 'supplier' && (
        <SupplierStagePanel
          project={liveProject}
          customer={customer}
          tasks={tasks}
          onProjectPatch={handleProjectPatch}
          onProductionDatesChange={handleProductionDatesChange}
          saving={saving}
          orderPlaced={tasks.some((task) => task.task === 'Order Goods' && task.completed)}
          onOrderPlacedChange={(placed) => handleTaskChange('Order Goods', placed)}
        />
      )}
      {activeStage === 'implementation' && (
        <ImplementationStagePanel
          project={liveProject || project}
          customer={customer}
          onProjectPatch={handleProjectPatch}
          onAttestedComplete={async () => {
            if (!(await areAllProjectJobCardsComplete((liveProject || project).id))) return;
            // Request Review stays open as the prompt to send the review email; sending it ticks the step
            await handleTaskChange('Installation Complete', true, { skipKeyDateSync: true });
          }}
          onReleased={async () => {
            await handleTaskChange('Installation Complete', false, { skipKeyDateSync: true });
          }}
        />
      )}
      {activeStage === 'aftercare' && (
        <AftercareStagePanel
          project={liveProject}
          tasks={tasks}
          supplyOnly={!!liveProject.supply_only}
          completing={saving}
          completeError={saveError}
          onCompleteWorkspace={async () => {
            await handleTaskChange('Complete Project', true, { skipKeyDateSync: true });
          }}
        />
      )}

      {activeStage !== 'quotes' && activeStage !== 'invoices' && (
        <Box sx={{ mt: 3 }}>
          <ProjectFolderViewer project={liveProject} />
        </Box>
      )}
    </Box>
  );
};

export default ProjectWorkspacePage;
